import React from "react";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit } from "react-icons/md";
import moment from "moment";
import {
    ModalContainer,
    ModalHeader,
    ModalContent,
    ScrollableContent,
    ModalSection,
    ModalRow,
    ModalRowLabel,
    ModalRowValue,
    ModalSectionTitle,
    ActionSection,
    SideCancelBtn,
    SubmitBtn,
} from "../../Styles/InputStyles";
import { capitalizeFirstLetter, getCategoryIcon, getFoodCategoryLabel, getFoodUnitLabel } from "../../../helpers";
import { FaBuilding, FaFileInvoice, FaBox, FaMoneyBillWave, FaCalendarAlt, FaUser } from "react-icons/fa";
import ModalProfileHeader from "../../Common/ModalProfileHeader";

const ViewPurchaseDetails = ({
    setShowPurchaseDetails,
    selectedData,
    canEdit = true,
}) => {

    const onClose = () => {
        setShowPurchaseDetails(false);
    };

    const entries = selectedData?.entries || [];
    const totalItems = entries.reduce((sum, entry) => sum + entry.quantity, 0);
    const hasDescription = selectedData?.description && selectedData?.description.trim() !== "";

    return (
        <ModalContainer>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Purchase Details</h1>
                <button onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <ModalProfileHeader
                title="Purchase Details"
                name={capitalizeFirstLetter(selectedData?.vendorName)}
                subtitle={`#${selectedData?.invoiceNo || 'N/A'}`}
                amount={selectedData?.totalAmount || 0}
                image={selectedData?.profilePicture}
                onClose={onClose}
            />
            <ModalContent>
                <ScrollableContent>
                    <Wrapper>
                        {/* Items Section */}
                        {/* <SectionWrapper> */}
                        <BtnWrapper>
                            <ModalSectionTitle>Items Purchased</ModalSectionTitle>
                        </BtnWrapper>

                        <ModalSection>
                            {entries.length > 0 ? (
                                <div className="space-y-3">
                                    {entries.map((entry, index) => {
                                        const totalPrice = entry.price;
                                        return (
                                            <ItemCard key={entry.id || index}>
                                                <div className="flex items-start gap-4">
                                                    {/* Item Details */}
                                                    <div className="flex-1 min-w-0">
                                                        <ItemName>
                                                            {capitalizeFirstLetter(entry.itemName)}
                                                        </ItemName>

                                                        <div className="flex items-center gap-2 mt-1">
                                                            <CategoryText>
                                                                {getFoodCategoryLabel(entry.categoryId)}
                                                            </CategoryText>

                                                            <Dot />

                                                            <QuantityText>
                                                                {entry.quantity} {getFoodUnitLabel(entry.unitType)}
                                                            </QuantityText>
                                                        </div>
                                                    </div>

                                                    {/* Amount */}
                                                    <Amount className="ml-auto">
                                                        ₹{Number(totalPrice || 0).toLocaleString('en-IN')}
                                                    </Amount>
                                                </div>
                                            </ItemCard>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-4 text-gray-500">No items found</div>
                            )}
                        </ModalSection>
                        {/* </SectionWrapper> */}
                        {hasDescription && (
                            <>
                                <BtnWrapper>
                                    <ModalSectionTitle>Description</ModalSectionTitle>
                                </BtnWrapper><DescriptionCard>
                                    <DescriptionText>{selectedData.description}</DescriptionText>
                                </DescriptionCard></>
                        )}
                        {/* Invoice Image Section */}
                        {selectedData?.invoice && (
                            <>
                                <ModalSectionTitle>Invoice Attachment</ModalSectionTitle>
                                <div >
                                    <button
                                        type="button"
                                        onClick={() => window.open(selectedData.invoice, "_blank")}
                                        className="relative group"
                                    >
                                        <img
                                            src={selectedData.invoice}
                                            alt="Invoice"
                                            className="
                                                w-32 h-32
                                                rounded-xl
                                                object-cover
                                                border border-gray-200
                                                shadow-sm
                                                transition-all
                                                duration-300
                                                group-hover:scale-105
                                                group-hover:shadow-lg
                                            "
                                        />
                                        <div
                                            className="
                                                absolute
                                                inset-0
                                                rounded-xl
                                                bg-black/0
                                                group-hover:bg-black/10
                                                transition
                                            "
                                        />
                                    </button>
                                </div>
                            </>
                        )}
                    </Wrapper>
                </ScrollableContent>
            </ModalContent>
        </ModalContainer>
    );
};

// Styled Components
const Wrapper = tw.div`grid gap-2 my-0`;
const SectionWrapper = tw.div`mt-2`;
const BtnWrapper = tw.div`w-full flex items-center justify-between`;
// const ItemCard = tw.div`
//     bg-gray-50 p-2 rounded-lg
//     border border-gray-100
//     hover:border-gray-200
//     transition-all duration-200
// `;
// const ItemName = tw.span`font-medium text-gray-700`;
const DescriptionCard = tw.div`
    bg-gray-50 p-4 rounded-lg
    border border-gray-100
 
`;
const DescriptionText = tw.p`
    text-sm text-gray-600
    leading-relaxed
    whitespace-pre-wrap
`;

const ItemCard = tw.div`
    w-full
    px-6
    py-4
    rounded-xl
    bg-white
    border
    border-gray-200
    transition-all
    duration-200
    hover:border-gray-300
    hover:shadow-sm
`;

const ItemIcon = tw.div`
    w-10
    h-10
    flex
    items-center
    justify-center
    flex-shrink-0
    rounded-lg
    bg-primary-color/10
    text-primary-color
`;

const ItemName = tw.div`
    text-sm
    font-semibold
    text-gray-800
    truncate
`;

const CategoryText = tw.span`
    text-xs
    text-gray-400
`;

const QuantityText = tw.span`
    text-xs
    font-medium
    text-gray-500
`;

const Dot = tw.span`
    w-1
    h-1
    rounded-full
    bg-gray-300
`;

const AmountLabel = tw.div`
    text-[10px]
    uppercase
    tracking-wide
    font-medium
    text-gray-400
`;

const Amount = tw.div`
    mt-0.5
    text-sm
    font-semibold
    text-gray-900
    tabular-nums
`;

export default ViewPurchaseDetails;